/**
 * 기능 제안 / 버그 신고 폼 — Mini App 표현층
 *
 * Slack 은 입력 모달로 받는다. 여기서는 같은 화면 안에서 탭으로 전환한다.
 * 저장 후에는 내가 낸 목록과 상태를 보여준다 — 모달은 제출하면 닫혀서 그게 안 된다.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { haptic } from '../../hooks/useTelegram';

const API_URL = '/api';

const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
});

const STATUS_LABEL = {
    pending: '접수됨',
    in_progress: '검토 중',
    completed: '완료',
    rejected: '반영 안 함',
};

export const RequestForm = () => {
    const [type, setType] = useState('feature');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [steps, setSteps] = useState('');
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState(null);
    const [mine, setMine] = useState([]);

    const loadMine = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/requests/mine`, {
                headers: authHeaders(),
            });
            setMine(data.requests || []);
        } catch (e) {
            /* 목록은 없어도 폼은 쓸 수 있다 */
        }
    };

    useEffect(() => {
        loadMine();
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!title.trim() || !description.trim()) {
            setError('제목과 내용을 입력해 주세요.');
            return;
        }
        setSending(true);
        setError(null);
        try {
            const body = { title, description };
            if (type === 'bug' && steps.trim()) body.steps_to_reproduce = steps;
            await axios.post(`${API_URL}/requests/${type}`, body, { headers: authHeaders() });
            haptic('medium');
            setTitle('');
            setDescription('');
            setSteps('');
            setDone(true);
            loadMine();
        } catch (err) {
            setError(err.response?.data?.detail || '전송에 실패했습니다.');
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="tg-screen">
            <div className="tg-titlebar">
                <h1 className="tg-title">의견 보내기</h1>
                <p className="tg-hint tg-date">개선 아이디어나 문제를 알려주세요.</p>
            </div>

            <div className="tg-tabs">
                {[
                    ['feature', '기능 제안'],
                    ['bug', '버그 신고'],
                ].map(([value, label]) => (
                    <button
                        key={value}
                        type="button"
                        className={`tg-tab${type === value ? ' is-active' : ''}`}
                        onClick={() => {
                            haptic('light');
                            setType(value);
                            setDone(false);
                        }}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {done && <p className="tg-notice">보내주셔서 감사합니다. 접수되었습니다.</p>}

            <form onSubmit={submit}>
                <label className="tg-label">제목</label>
                <input
                    className="tg-input"
                    value={title}
                    maxLength={200}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={type === 'feature' ? '어떤 기능을 원하시나요?' : '어떤 문제가 있었나요?'}
                />

                <label className="tg-label">내용</label>
                <textarea
                    className="tg-input tg-textarea"
                    value={description}
                    maxLength={4000}
                    rows={5}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="자세히 적어주실수록 도움이 됩니다."
                />

                {type === 'bug' && (
                    <>
                        <label className="tg-label">재현 방법 (선택)</label>
                        <textarea
                            className="tg-input tg-textarea"
                            value={steps}
                            maxLength={4000}
                            rows={3}
                            onChange={(e) => setSteps(e.target.value)}
                            placeholder="어떤 순서로 하면 다시 발생하나요?"
                        />
                    </>
                )}

                {error && <p className="tg-error">{error}</p>}

                <button type="submit" className="tg-button tg-submit" disabled={sending}>
                    {sending ? '보내는 중…' : '보내기'}
                </button>
            </form>

            {mine.length > 0 && (
                <section className="tg-survey">
                    <h3 className="tg-survey-title">내가 보낸 의견</h3>
                    <ul className="tg-list">
                        {mine.map((r) => (
                            <li key={`${r.type}-${r.id}`} className="tg-list-item">
                                <span className="tg-badge">
                                    {r.type === 'bug' ? '버그' : '제안'}
                                </span>
                                <span className="tg-list-title">{r.title}</span>
                                <span className="tg-hint">
                                    {STATUS_LABEL[r.status] || r.status}
                                </span>
                            </li>
                        ))}
                    </ul>
                </section>
            )}
        </div>
    );
};

export default RequestForm;
